/**
 * Firestore Upload Validation Utility
 * Validates data types and ranges for Serie, Exercise, Workout, and Block uploadToCloud() methods
 *
 * Schema-based validation - edit the schema objects below to configure validation rules
 */

// ============================================================================
// SCHEMA DEFINITIONS - Edit these to configure validation rules
// ============================================================================

/**
 * Serie schema definition
 * @type {Object}
 */
const serieSchema = {
  id: { type: "string", required: true },
  order: { type: "integer", required: true, min: 0 },
  reps: { type: "integer", required: true },
  rpe: { type: "float", required: true, min: 0, max: 10 },
  weight: { type: "float", required: true, min: 0 },
  target_reps: { type: "string|integer", required: false }, // Can be string or integer
  target_rpe: { type: "string|float", required: false }, // Can be string or float
  target_weight: { type: "string|float", required: false }, // Can be string or float
  relevance: { type: "integer", required: true, min: 1, max: 3 },
  has_video: { type: "boolean", required: false },
  require_video: { type: "boolean", required: false },
};

/**
 * Exercise schema definition
 * @type {Object}
 */
const exerciseSchema = {
  id: { type: "string", required: true },
  name: { type: "string", required: true },
  notes: { type: "string", required: true },
  order: { type: "integer", required: true, min: 0 },
  original_name: { type: "string", required: true },
  series: { type: "array", required: true, itemSchema: "serie" }, // Nested validation
  attributes: { type: "array", required: true },
  is_secondary_protocol: { type: "boolean", required: false },
  protocol_slot: { type: "string|null", required: false }, // Can be string or null
  protocol_text: { type: "string", required: false },
  protocol_week: { type: "integer|null", required: false }, // Can be integer or null
  link: { type: "string", required: false },
};

/**
 * Workout schema definition
 * @type {Object}
 */
const workoutSchema = {
  id: { type: "string", required: true },
  date: { type: "Date|null", required: false }, // Can be Date or null
  name: { type: "string", required: true },
  order: { type: "integer", required: true, min: 0 },
  week: { type: "integer", required: true, min: 0, max: 24 },
  exercises: { type: "array", required: true, itemSchema: "exercise" }, // Nested validation
  notes: { type: "string", required: false },
  last_update: { type: "Date", required: false },
  device_os: { type: "string", required: true, value: "web" }, // Exact value match
};

/**
 * Block schema definition
 * @type {Object}
 */
const blockSchema = {
  id: { type: "string", required: true },
  name: { type: "string", required: true },
  notes: { type: "string", required: true },
  weeks: { type: "integer", required: false, min: 0 },
  total_weeks: { type: "integer", required: true, min: 0 },
  label: { type: "string", required: false },
  workouts: { type: "array", required: false, itemSchema: "workout" }, // Nested validation
  protocols: { type: "array", required: false, itemSchema: "exercise" }, // Nested validation
  hidden_weeks: { type: "array", required: false },
  week_names: { type: "object", required: false },
  modified_by: { type: "string", required: false },
  calendar_start_date: { type: "Date|null", required: false }, // Can be Date or null
  effective_start_date: { type: "Date|null", required: false },
  effective_end_date: { type: "Date|null", required: false },
  max_created_week: { type: "integer", required: false, min: 0 },
  last_update: { type: "Date", required: false },
};

// ============================================================================
// VALIDATION ENGINE - Generic validator that uses schemas
// ============================================================================

/**
 * Schema registry for nested validation
 */
const schemaRegistry = {
  serie: serieSchema,
  exercise: exerciseSchema,
  workout: workoutSchema,
  block: blockSchema,
};

/**
 * Validates if a value matches a specific type
 * @param {*} value - The value to check
 * @param {string} type - The type to check against
 * @param {Object} fieldSchema - Schema definition (for range checks)
 * @returns {boolean} True if value matches the type
 */
function validateType(value, type, fieldSchema) {
  switch (type) {
    case "string":
      return typeof value === "string";

    case "integer":
      return typeof value === "number" && Number.isInteger(value);

    case "float":
    case "number":
      return typeof value === "number";

    case "boolean":
      return typeof value === "boolean";

    case "Date":
      return value instanceof Date;

    case "array":
      return Array.isArray(value);

    case "object":
      return (
        typeof value === "object" && value !== null && !Array.isArray(value)
      );

    default:
      return false;
  }
}

/**
 * Validates numeric range constraints
 * @param {number} value - The numeric value
 * @param {Object} fieldSchema - Schema definition with min/max
 * @param {string} fullPath - Full property path for error messages
 * @returns {string[]} Array of error messages (empty if valid)
 */
function validateRange(value, fieldSchema, fullPath) {
  const errors = [];

  if (fieldSchema.min !== undefined && value < fieldSchema.min) {
    errors.push(
      `Property '${fullPath}' value ${value} is outside allowed range [${fieldSchema.min}, ${fieldSchema.max !== undefined ? fieldSchema.max : "Infinity"}]`,
    );
  }
  if (fieldSchema.max !== undefined && value > fieldSchema.max) {
    errors.push(
      `Property '${fullPath}' value ${value} is outside allowed range [${fieldSchema.min !== undefined ? fieldSchema.min : "-Infinity"}, ${fieldSchema.max}]`,
    );
  }

  return errors;
}

/**
 * Validates a single property value against its schema definition
 * @param {*} value - The value to validate
 * @param {string} propertyName - Name of the property (for error messages)
 * @param {Object} fieldSchema - Schema definition for this field
 * @param {string} parentPath - Path prefix for nested errors (e.g., "series[0]")
 * @returns {string[]} Array of error messages (empty if valid)
 */
function validateField(value, propertyName, fieldSchema, parentPath = "") {
  const errors = [];
  const fullPath = parentPath ? `${parentPath}.${propertyName}` : propertyName;

  // Check if field is required
  if (fieldSchema.required && (value === undefined || value === null)) {
    errors.push(
      `Property '${fullPath}' is required but is ${value === undefined ? "undefined" : "null"}`,
    );
    return errors; // Early return - can't validate type if missing
  }

  // Skip validation if field is optional and not present
  if (!fieldSchema.required && (value === undefined || value === null)) {
    return errors; // Valid - optional field can be undefined/null
  }

  // Handle union types (e.g., "string|null", "string|integer", "string|float")
  const typeDef = fieldSchema.type;

  // Defensive check: ensure typeDef exists and is a string
  if (!typeDef || typeof typeDef !== "string") {
    errors.push(
      `Property '${fullPath}' has invalid schema: missing or invalid type definition`,
    );
    return errors;
  }

  const isNullable = typeDef.includes("|null");

  // Check null for nullable types
  if (isNullable && value === null) {
    return errors; // Valid - null is allowed
  }

  // Parse union types (split by | and filter out null)
  const typeParts = typeDef.split("|").filter((part) => part !== "null");

  // Defensive check: ensure we have at least one type part
  if (typeParts.length === 0) {
    errors.push(
      `Property '${fullPath}' has invalid schema: no valid type definition`,
    );
    return errors;
  }

  const isUnionType = typeParts.length > 1;

  // If union type, validate against any matching type
  if (isUnionType) {
    let isValid = false;
    let matchedType = null;

    for (const typePart of typeParts) {
      if (validateType(value, typePart, fieldSchema)) {
        isValid = true;
        matchedType = typePart;
        break;
      }
    }

    if (!isValid) {
      const expectedTypes = typeParts.join(" or ");
      errors.push(
        `Property '${fullPath}' has invalid format: expected ${expectedTypes}${isNullable ? " or null" : ""}, got ${typeof value}`,
      );
    } else {
      // If valid, check range constraints (for numeric types)
      if (
        matchedType === "integer" ||
        matchedType === "float" ||
        matchedType === "number"
      ) {
        const rangeErrors = validateRange(value, fieldSchema, fullPath);
        errors.push(...rangeErrors);
      }
    }

    return errors;
  }

  // Single type validation (original logic)
  const baseType = typeParts[0];

  // Type validation
  switch (baseType) {
    case "string":
      if (typeof value !== "string") {
        errors.push(
          `Property '${fullPath}' has invalid format: expected string${isNullable ? " or null" : ""}, got ${typeof value}`,
        );
      }
      break;

    case "integer":
      if (typeof value !== "number" || !Number.isInteger(value)) {
        errors.push(
          `Property '${fullPath}' has invalid format: expected integer${isNullable ? " or null" : ""}, got ${typeof value}`,
        );
      } else {
        // Range validation for integers
        const rangeErrors = validateRange(value, fieldSchema, fullPath);
        errors.push(...rangeErrors);
      }
      break;

    case "float":
    case "number":
      if (typeof value !== "number") {
        errors.push(
          `Property '${fullPath}' has invalid format: expected number${isNullable ? " or null" : ""}, got ${typeof value}`,
        );
      } else {
        // Range validation for numbers
        const rangeErrors = validateRange(value, fieldSchema, fullPath);
        errors.push(...rangeErrors);
      }
      break;

    case "boolean":
      if (typeof value !== "boolean") {
        errors.push(
          `Property '${fullPath}' has invalid format: expected boolean${isNullable ? " or null" : ""}, got ${typeof value}`,
        );
      }
      break;

    case "Date":
      if (!(value instanceof Date)) {
        errors.push(
          `Property '${fullPath}' has invalid format: expected Date${isNullable ? " or null" : ""}, got ${typeof value}`,
        );
      }
      break;

    case "array":
      if (!Array.isArray(value)) {
        errors.push(
          `Property '${fullPath}' has invalid format: expected array, got ${typeof value}`,
        );
      } else if (fieldSchema.itemSchema) {
        // Nested validation for array items
        const nestedSchemaName = fieldSchema.itemSchema;
        const nestedSchema = schemaRegistry[nestedSchemaName];
        if (nestedSchema) {
          value.forEach((item, index) => {
            const itemErrors = validateObject(
              item,
              nestedSchema,
              `${fullPath}[${index}]`,
            );
            errors.push(...itemErrors);
          });
        }
      }
      break;

    case "object":
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        errors.push(
          `Property '${fullPath}' has invalid format: expected object, got ${typeof value}`,
        );
      }
      break;

    default:
      errors.push(
        `Property '${fullPath}' has unknown type definition: ${baseType}`,
      );
  }

  // Exact value validation (e.g., device_os: "web")
  if (fieldSchema.value !== undefined && value !== fieldSchema.value) {
    errors.push(
      `Property '${fullPath}' has invalid value: expected '${fieldSchema.value}', got '${value}'`,
    );
  }

  return errors;
}

/**
 * Validates an object against a schema definition
 * @param {Object} data - The data object to validate
 * @param {Object} schema - Schema definition object
 * @param {string} parentPath - Path prefix for nested errors
 * @returns {string[]} Array of error messages (empty if valid)
 */
function validateObject(data, schema, parentPath = "") {
  const errors = [];

  // Defensive check: ensure data and schema are valid objects
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    errors.push(
      `Invalid data object at '${parentPath || "root"}': expected object, got ${typeof data}`,
    );
    return errors;
  }

  if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
    errors.push(
      `Invalid schema definition at '${parentPath || "root"}': expected object, got ${typeof schema}`,
    );
    return errors;
  }

  // Validate each field in the schema
  for (const [propertyName, fieldSchema] of Object.entries(schema)) {
    // Defensive check: ensure fieldSchema is valid
    if (!fieldSchema || typeof fieldSchema !== "object") {
      errors.push(
        `Invalid field schema for '${parentPath ? `${parentPath}.${propertyName}` : propertyName}': expected object`,
      );
      continue;
    }

    const value = data[propertyName];
    const fieldErrors = validateField(
      value,
      propertyName,
      fieldSchema,
      parentPath,
    );
    errors.push(...fieldErrors);
  }

  return errors;
}

// ============================================================================
// PUBLIC VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validates a Serie object
 * @param {Object} data - The Serie data object from uploadToCloud()
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validateSerie(data) {
  try {
    const errors = validateObject(data, serieSchema);
    return {
      valid: errors.length === 0,
      errors,
    };
  } catch (error) {
    // Defensive: catch any unexpected errors to prevent crashes
    return {
      valid: false,
      errors: [`Validation error: ${error.message || String(error)}`],
    };
  }
}

/**
 * Validates an Exercise object
 * @param {Object} data - The Exercise data object from uploadToCloud()
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validateExercise(data) {
  try {
    const errors = validateObject(data, exerciseSchema);
    return {
      valid: errors.length === 0,
      errors,
    };
  } catch (error) {
    // Defensive: catch any unexpected errors to prevent crashes
    return {
      valid: false,
      errors: [`Validation error: ${error.message || String(error)}`],
    };
  }
}

/**
 * Validates a Workout object
 * @param {Object} data - The Workout data object from uploadToCloud()
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validateWorkout(data) {
  try {
    const errors = validateObject(data, workoutSchema);
    return {
      valid: errors.length === 0,
      errors,
    };
  } catch (error) {
    // Defensive: catch any unexpected errors to prevent crashes
    return {
      valid: false,
      errors: [`Validation error: ${error.message || String(error)}`],
    };
  }
}

/**
 * Validates a Block object
 * @param {Object} data - The Block data object from uploadToCloud()
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validateBlock(data) {
  try {
    const errors = validateObject(data, blockSchema);
    return {
      valid: errors.length === 0,
      errors,
    };
  } catch (error) {
    // Defensive: catch any unexpected errors to prevent crashes
    return {
      valid: false,
      errors: [`Validation error: ${error.message || String(error)}`],
    };
  }
}
